import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Globe } from "lucide-react";
import AuroraMark from "@/components/AuroraMark";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";

const DEFAULT_LINKS = [
  { order: 1, label: "Katalog", href: "/#katalog" },
  { order: 2, label: "Program Kemitraan", href: "/#tier" },
  { order: 3, label: "Workshop", href: "/#workshop" },
  { order: 4, label: "E-Learning", href: "/e-learning" },
  { order: 5, label: "Kritik & Saran", href: "/kritik-saran" },
  { order: 6, label: "Daftar", href: "/#daftar" },
];

// Map canonical hrefs → i18n keys so navbar labels toggle correctly with the
// language switch. CMS-managed labels (from GET /nav) are also mapped by href
// when they point to one of these standard destinations.
const NAV_I18N_KEY = {
  "/#katalog": "nav.catalog",
  "/#tier": "nav.partnership",
  "/#workshop": "nav.workshop",
  "/e-learning": "nav.elearning",
  "/kritik-saran": "nav.feedback",
  "/#daftar": "nav.register",
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [showFloatCTA, setShowFloatCTA] = useState(false);
  const [open, setOpen] = useState(false);
  const [links, setLinks] = useState(DEFAULT_LINKS);
  const [activeHash, setActiveHash] = useState("");
  const location = useLocation();
  const { locale, setLocale, t } = useI18n();

  // Smart scroll behavior: hide navbar on scroll-down, show floating CTA;
  // reverse on scroll-up. Never hide when near the top of the page.
  useEffect(() => {
    let lastY = window.scrollY;
    let ticking = false;
    const THRESHOLD = 8; // ignore tiny scroll jitter
    const TOP_ZONE = 80; // never hide navbar within first 80px
    const CTA_TRIGGER = 480; // only reveal floating CTA after user scrolled past hero
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const y = window.scrollY;
        setScrolled(y > 20);
        const delta = y - lastY;
        if (Math.abs(delta) > THRESHOLD) {
          if (delta > 0 && y > TOP_ZONE) {
            // scrolling DOWN
            setHidden(true);
            setShowFloatCTA(y > CTA_TRIGGER);
          } else if (delta < 0) {
            // scrolling UP
            setHidden(false);
            setShowFloatCTA(false);
          }
          lastY = y;
        }
        if (y <= TOP_ZONE) {
          setHidden(false);
          setShowFloatCTA(false);
        }
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    api.get("/nav").then((r) => Array.isArray(r.data) && r.data.length && setLinks(r.data)).catch(() => {});
  }, []);

  // Scroll-spy for hash sections on home page
  useEffect(() => {
    if (location.pathname !== "/") {
      setActiveHash("");
      return;
    }
    const sectionIds = links
      .map((l) => l.href)
      .filter((h) => h.startsWith("/#"))
      .map((h) => h.replace("/#", ""));
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible.length) setActiveHash("#" + visible[0].target.id);
      },
      { rootMargin: "-40% 0px -50% 0px", threshold: [0.1, 0.5, 1] }
    );
    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [location.pathname, links]);

  const isActive = (href) => {
    if (href.startsWith("/#")) {
      return location.pathname === "/" && activeHash === href.replace("/", "");
    }
    return location.pathname === href;
  };

  const testid = (l) => `nav-link-${(l.label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`;

  // Resolve display label: prefer i18n translation when the href is one of the
  // canonical destinations; otherwise fall back to the CMS/DEFAULT label.
  const linkLabel = (l) => {
    const key = NAV_I18N_KEY[(l.href || "").toLowerCase()];
    return key ? t(key, l.label) : l.label;
  };

  // Filter out the standalone "Daftar" link from desktop nav (redundant with black CTA button).
  // We match by href to be robust against label edits ("Daftar", "Daftar Sekarang", etc.).
  const desktopLinks = links
    .filter((l) => (l.enabled === undefined ? true : l.enabled))
    .filter((l) => {
      const href = (l.href || "").toLowerCase();
      const label = (l.label || "").trim().toLowerCase();
      // hide if it points at the register anchor AND the label is a bare "Daftar" (not "Daftar Kemitraan")
      return !(href === "/#daftar" && label === "daftar");
    });

  const logoClick = (e) => {
    if (location.pathname === "/") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <>
    <header
      data-testid="site-navbar"
      data-hidden={hidden ? "true" : "false"}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-out ${
        hidden ? "-translate-y-full opacity-0 pointer-events-none" : "translate-y-0 opacity-100"
      } ${
        scrolled ? "backdrop-blur-xl bg-[#FDFBF7]/85 border-b border-stone-200/70" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-3 md:py-4 flex items-center justify-between">
        <a
          href="/"
          onClick={logoClick}
          data-testid="navbar-logo"
          className="flex items-center gap-2 md:gap-3 cursor-pointer group"
          aria-label={t("nav.cta", "Aurora Hijab")}
        >
          <div className="transition-transform duration-300 group-hover:scale-110">
            <AuroraMark size="h-11 w-11 md:h-14 md:w-14" />
          </div>
          <div className="leading-tight">
            <div className="font-display text-lg md:text-xl tracking-tight text-[#0A0A0A]">
              Aurora <span className="text-[#F39237]">Hijab</span>
            </div>
            <div className="overline text-[10px] md:text-[11px] text-[#F39237]">Simple &amp; Adorable</div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-7 xl:gap-10 ml-auto mr-8">
          {desktopLinks.map((l) => (
            <a
              key={l.href + l.label}
              href={l.href}
              data-testid={testid(l)}
              className={`text-sm tracking-wide relative transition-colors ${
                isActive(l.href) ? "text-[#F39237]" : "text-stone-700 hover:text-[#F39237]"
              }`}
            >
              {linkLabel(l)}
              <span
                className={`absolute left-0 -bottom-1.5 h-0.5 bg-[#F39237] transition-all duration-300 ${
                  isActive(l.href) ? "w-full" : "w-0"
                }`}
                aria-hidden="true"
              />
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <button
            onClick={() => setLocale(locale === "id" ? "en" : "id")}
            className="inline-flex items-center gap-1 text-xs tracking-wide text-stone-600 hover:text-[#F39237] transition-colors uppercase"
            data-testid="lang-toggle"
            aria-label="Toggle language"
          >
            <Globe size={14} />
            {locale.toUpperCase()}
          </button>
          <a
            href="/#daftar"
            data-testid="navbar-cta-daftar"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0A0A0A] text-white text-sm tracking-wide hover:bg-[#F39237] hover:text-[#0A0A0A] hover:-translate-y-0.5 transition-all shadow-sm hover:shadow-md"
          >
            {t("nav.cta", "Daftar Kemitraan")}
          </a>
        </div>

        <button
          data-testid="navbar-mobile-toggle"
          className={`lg:hidden p-2 hamburger text-[#0A0A0A] ${open ? "is-open" : ""}`}
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
          aria-expanded={open}
        >
          <span className="hamburger-line" />
          <span className="hamburger-line" />
          <span className="hamburger-line" />
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#FDFBF7] border-t border-stone-200">
          <nav className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.href + l.label}
                href={l.href}
                data-testid={`mobile-${testid(l)}`}
                className={`text-base ${isActive(l.href) ? "text-[#F39237] font-medium" : "text-stone-700"}`}
              >
                {linkLabel(l)}
              </a>
            ))}
            <div className="flex items-center justify-between pt-3 border-t border-stone-200">
              <button
                onClick={() => setLocale(locale === "id" ? "en" : "id")}
                className="inline-flex items-center gap-1 text-xs uppercase text-stone-600"
                data-testid="mobile-lang-toggle"
              >
                <Globe size={14} /> {locale.toUpperCase()}
              </button>
              <a
                href="/#daftar"
                data-testid="mobile-navbar-cta-daftar"
                className="inline-flex items-center justify-center px-5 py-3 bg-[#0A0A0A] text-white text-sm"
              >
                {t("nav.cta", "Daftar Kemitraan")}
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>

    {/* Floating CTA — appears when navbar is hidden during scroll-down.
        Positioned to the right, directly beneath the navbar's "Daftar Kemitraan" button. */}
    <a
      href="/#daftar"
      data-testid="floating-cta-daftar"
      aria-hidden={!showFloatCTA}
      tabIndex={showFloatCTA ? 0 : -1}
      className={`inline-flex fixed top-[76px] md:top-[88px] right-4 md:right-8 z-[60] items-center gap-2 px-5 md:px-6 py-3 md:py-3.5 rounded-full bg-[#0A0A0A] text-white text-sm tracking-wide shadow-xl shadow-black/20 hover:bg-[#F39237] hover:text-[#0A0A0A] transition-all duration-500 ease-out whitespace-nowrap ${
        showFloatCTA ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-3 pointer-events-none"
      }`}
    >
      <span className="inline-block w-2 h-2 rounded-full bg-[#F39237] animate-pulse" aria-hidden="true" />
      {t("nav.cta", "Daftar Kemitraan")}
    </a>
    </>
  );
}
